import { formatTime, isTimePast } from '@lib/server';
import { IconInfoCircle } from '@tabler/icons-react';
import type { OpeningTime, SpecialTimetable, Timetable } from '@types';

import { defaultTitle } from './opening-times';
import { Text } from './text';

export type TimetableProps = {
  name: string;
  content?: Timetable[];
  special?: SpecialTimetable[];
  type?: string[];
  notes?: string;
};

function TimeRange({ openingHour, closingHour }: { openingHour: string; closingHour: string }) {
  return (
    <>
      <time dateTime={openingHour}>{openingHour}</time>
      {' - '}
      <time dateTime={closingHour}>{closingHour}</time>
    </>
  );
}

function OpeningHours({ isOpen, times, name }: { isOpen: boolean; times: OpeningTime[]; name: string }) {
  const formatOpeningHours = (time: OpeningTime) => ({
    openingHour: formatTime(time?.openingHour || undefined, 'h:mm aaa') || '9:00 am',
    closingHour: formatTime(time?.closingHour || undefined, 'h:mm aaa') || '11:00 pm',
  });

  if (!isOpen) {
    return (
      <Text element="p" className="opacity-50">
        {name === defaultTitle ? 'Closed' : `No ${name}`}
      </Text>
    );
  }

  return times.map((time, k) => {
    const { openingHour, closingHour } = formatOpeningHours(time);

    return (
      <Text element="p" key={k}>
        <TimeRange openingHour={openingHour} closingHour={closingHour} />
      </Text>
    );
  });
}

function RegularTimetable({
  timetable,
  currentDate,
  currentDay,
}: {
  timetable: TimetableProps;
  currentDate: Date;
  currentDay: number;
}) {
  return timetable.content?.map((day, j) => {
    // Calculate the target date for the current day in the timetable
    const targetDate = new Date(currentDate);
    const dayOfWeekNumber = day.dayOfWeekNumber === 0 ? 7 : day.dayOfWeekNumber || 0;
    targetDate.setDate(currentDate.getDate() + dayOfWeekNumber - currentDay);

    // Check if there's a special timetable for the target date
    const hasSpecial = timetable.special?.find((special) => {
      return (
        formatTime({ seconds: targetDate.getTime() / 1000 }, 'd MMM yyyy') ===
        formatTime(special.openingTimes?.[0]?.date, 'd MMM yyyy')
      );
    });

    const displayDay = hasSpecial || day;
    const isCurrentDay = currentDay === day.dayOfWeekNumber;

    return (
      <div key={j} className={`w-full py-[calc(0.43rem-1px)] ${isCurrentDay ? 'font-bold' : ''}`}>
        <div className={`flex w-full shrink-0 justify-between gap-4 py-0.5 ${isCurrentDay ? 'font-bold' : ''}`}>
          <Text element="p" className="flex flex-col">
            {day.dayOfWeek}
            {hasSpecial && isTimePast(hasSpecial?.openingTimes?.[0]?.date) && (
              <Text element="span" className="text-xs! font-medium">
                {hasSpecial.label && `${hasSpecial.label} - `}
                {formatTime(hasSpecial.openingTimes[0].date, 'd MMM yyyy')}
              </Text>
            )}
          </Text>
          <div className="flex flex-col items-end text-right">
            <OpeningHours isOpen={displayDay.isOpen} times={displayDay.openingTimes} name={timetable.name} />
          </div>
        </div>
      </div>
    );
  });
}

function SpecialTimetable({ timetable }: { timetable: TimetableProps }) {
  const currentDate = new Date();

  return timetable.special
    ?.filter((special) => {
      return (special.openingTimes?.[0].date?.seconds || 0) > currentDate.getTime() / 1000;
    })
    .map((special, k) => {
      const date = formatTime(special.openingTimes?.[0]?.date, 'd MMM yyyy');

      return (
        <div key={k} className="flex w-full justify-between gap-4 py-[calc(0.43rem-1px)]">
          <Text className="flex flex-col" element="p">
            {special.label || date}
            {special.label && (
              <Text element="span" className="text-xs! font-medium">
                {date}
              </Text>
            )}
          </Text>
          <OpeningHours isOpen={special.isOpen} times={special.openingTimes} name={timetable.name} />
        </div>
      );
    });
}

export function Timetable({ timetable, special = false }: { timetable?: TimetableProps; special?: boolean }) {
  if (!timetable) return null;

  const currentDate = new Date();
  const currentDay = currentDate.getDay();

  return (
    <div className="flex flex-col gap-1">
      <div className="divide-border/50 flex flex-col divide-y">
        {!special ? (
          <RegularTimetable timetable={timetable} currentDate={currentDate} currentDay={currentDay} />
        ) : (
          <SpecialTimetable timetable={timetable} />
        )}
      </div>

      {timetable.notes && (
        <div className="border-muted-foreground/10 text-muted-foreground bg-muted/10 flex items-center gap-2 rounded-sm border px-2 py-1">
          <IconInfoCircle size={20} className="opacity-50" />
          <Text element="div" className="text-xs font-medium">
            {timetable.notes}
          </Text>
        </div>
      )}
    </div>
  );
}
